export interface SendableCandidate {
  id: string
  status?: string
  past_deadline?: boolean | null
}

export interface SendingGroups {
  /** Never invited. The invitation is what sets their deadline. */
  invitees: SendableCandidate[]
  /** Invited, still able to act. A reminder is only a reminder. */
  remindees: SendableCandidate[]
  /** Invited, past the deadline. Sending moves the deadline to the new date. */
  expired: SendableCandidate[]
}

const NOT_YET_INVITED = 'created'

export const groupForSending = (
  items?: Record<string, SendableCandidate> | null,
  selected: Record<string, unknown> = {},
): SendingGroups => {
  const ticked = Object.keys(selected ?? {})
  // An empty selection means the whole tab, which is what the two-group version did: the header
  // buttons act on everything until a recruiter ticks a row.
  const chosen = Object.values(items ?? {}).filter(
    candidate => ticked.length === 0 || ticked.includes(candidate?.id),
  )

  const groups: SendingGroups = { invitees: [], remindees: [], expired: [] }
  for (const candidate of chosen) {
    if (candidate?.status === NOT_YET_INVITED) {
      // Never invited, so there is no deadline yet to have passed. Reached before the expiry
      // check on purpose: a stale `past_deadline` on such a row must not move them.
      groups.invitees.push(candidate)
    } else if (candidate?.past_deadline) {
      groups.expired.push(candidate)
    } else {
      // Includes a payload with no `past_deadline` field at all. Still in time is the safe
      // reading: guessing "expired" would claim that sending extends a deadline when it does not.
      groups.remindees.push(candidate)
    }
  }
  return groups
}

/** Whether the send about to happen will move a deadline rather than only remind.
 *
 * Derived from the list being sent to, not from which button opened the modal. A per-row send
 * and a group send then answer the same way, and there is no second piece of state to keep in
 * agreement with the first.
 */
export const willExtendDeadline = (
  candidates?: SendableCandidate[] | null,
): boolean => Boolean(candidates?.some(candidate => candidate?.past_deadline))
